(async () => {
  const { updateURLParams, historyPush, parseHTML } = await import(window.theme.modules.utils);
  const baseComponent = await import(window.theme.modules.baseComponent);

  const isEqual = (a, b) => a && b && a.toString() === b.toString();
  const strEsc = (str) => String(str).replace(/["\\]/g, '\\$&');
  const parseJSON = (el) => JSON.parse(el.textContent || 'null');

  customElements.define('product-variant-selector', class extends baseComponent.default {
    elements = {
      variantsJSON: '[data-variants-json]',
      optionsJSON: '[data-options-with-values]'
    };

    /* Helpers */
    _selectedOrVariantOptions() {
      if (this.selectedOptions?.length) return this.selectedOptions;
      return this.variant?.options ? [...this.variant.options] : [];
    }

    _findVariantOrNull(options) {
      if (!options?.length) return null;
      return this.variantsData.find(v => isEqual(v.options, options)) || null;
    }

    _isVariantHiddenByMetafield(variant) {
      const mv = variant?.metafields?.custom?.hide_variant;
      if (mv == null) return false;

      if (typeof mv === 'object') {
        if ('value' in mv) {
          const val = mv.value;
          if (typeof val === 'boolean') return val;
          if (typeof val === 'number') return val === 1;
          if (typeof val === 'string') {
            const s = val.trim().toLowerCase();
            return s === 'true' || s === '1' || s === 'yes';
          }
        }
        if ('boolean_value' in mv) return !!mv.boolean_value;
        if ('presentment_value' in mv) return !!mv.presentment_value;
        return false;
      }
      if (typeof mv === 'boolean') return mv;
      if (typeof mv === 'number') return mv === 1;
      if (typeof mv === 'string') {
        const s = mv.trim().toLowerCase();
        return s === 'true' || s === '1' || s === 'yes';
      }
      return false;
    }

    /* Lifecycle */
    render() {
      this.cache = {};
      this.isProductPage = this.hasAttribute('product-page');
      this.variantsData = parseJSON(this.$variantsJSON) || [];
      this.optionsData  = parseJSON(this.$optionsJSON)  || [];

      this._getDefaultVariant();
      this.selectedOptions = [...(this.variant?.options || [])];

      this._checkUnavailable();
      this.addEventListener('change', this._variantChangeHandler.bind(this));

      this.on('qtyStatusUpdate', (e, data) => {
        this.cache[+data.variantId] = data.html.outerHTML;
      }, false);

      this._setInititalCache();
    }

    _getDefaultVariant() {
      const currentId = +this.getAttribute('current-variant-id');
      this.variant = this.variantsData.find(v => v.id === currentId) || this.variantsData[0] || null;
    }

    _setInititalCache() {
      const parentSection = (this.getParentSection && this.getParentSection()) || this.closest('section[id^="shopify-section"]');
      if (parentSection && this.variant?.id) {
        this.cache[this.variant.id] = parentSection.outerHTML;
      }
    }

    /* Availability UI */
    _checkUnavailable() {
      const base = [...this._selectedOrVariantOptions()];
      this.optionsData.forEach((option, i) => {
        option.values.forEach(optionValue => {
          const test = [...base];
          test[i] = optionValue;
          const match = this._findVariantOrNull(test);
          this._toggleOption(option.name, optionValue, !match);
        });
      });
    }

    _toggleOption(name, value, disable) {
      const sel = `[name="${strEsc(name)}"][value="${strEsc(value)}"]`;
      const targetInput = this.querySelector(sel);
      if (!targetInput) return;
      if (disable) targetInput.setAttribute('data-unavailable', '');
      else targetInput.removeAttribute('data-unavailable');
    }

    /* Change handling */
    _variantChangeHandler(e) {
      if (e.detail?.synthetic) return;

      this._selectVariantByOption(e.target);
      this._checkUnavailable();

      const resolved = this.variant;
      const isHidden = this._isVariantHiddenByMetafield(resolved);

      if (!resolved || isHidden) {
        this.forwardEvent('variantInvalid', {
          selectedOptions: this.selectedOptions,
          hidden: !!isHidden
        });
        return;
      }

      const { queryURL, currentURL } = updateURLParams(
        { variant: resolved.id },
        this.sectionId,
        window.location.origin + this.getAttribute('product-url')
      );

      if (this.isProductPage) historyPush(currentURL);

      const cached = this.cache[resolved.id];
      if (cached) {
        this.forwardEvent('variantChange', { html: parseHTML(cached), variant: resolved });
      } else {
        this._fetchPageWithVariant(queryURL, (html) => {
          this.cache[resolved.id] = html;
          this.forwardEvent('variantChange', { html: parseHTML(html), variant: resolved });
        });
      }
    }

    _selectVariantByOption(target) {
      const idx = this.optionsData.findIndex(option => option.name === target.name);
      const base = [...this._selectedOrVariantOptions()];
      base[idx] = target.value;
      this.selectedOptions = base;
      this.variant = this._findVariantOrNull(this.selectedOptions); // may be null (prevents auto-jump)
    }

    /* Fetch */
    _fetchPageWithVariant(url, onResponse) {
      this.forwardEvent('variantLoading');
      return fetch(url)
        .then(r => r.text())
        .then(html => { onResponse(html); });
    }
  });

  customElements.define('product-option-picker', class extends baseComponent.default {
    elements = { value: '[data-option-value]' };
    render() {
      this.addEventListener('change', e => {
        if (this.$value) this.$value.innerText = e.target.value;
      });
    }
  });
})();
